import {
  Avatar,
  Box,
  Button,
  HStack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import { type Maybe, TimeBillingTypeEnum } from "@src/__generated__/graphql";
import { NumberInput, TimeInput } from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { currency } from "@src/utils/formatters";
import type { NumberModel, TimeFieldState } from "@src/utils/models";
import { secondsToTime } from "@src/utils/time";
import { observer } from "mobx-react-lite";
import type { ExpandedRowStore } from "./expanded-row/store";

type UserRow = {
  id: string;
  user: Maybe<{
    id: string;
    full_name: string;
    image?: { urls: { thumbnail?: string | null } } | null;
  }>;
  position: { id: string; title: string } | null;
  team: { id: string; name: string } | null;
  tracked_time: number;
  client_rate: NumberModel;
  spent: NumberModel;
  effective_hourly_rate: NumberModel;
  amount: NumberModel;
  invoiced_time: TimeFieldState;
  overserviced_time: TimeFieldState;
  effective_hourly_rate_invoiced: NumberModel;
};

type Props = {
  readonly rowStore: ExpandedRowStore;
};

function getRowKey(row: UserRow): string {
  return row.id;
}

function getThumbnailUrl(row: UserRow): string | undefined {
  return row.user?.image?.urls.thumbnail ?? undefined;
}

export const UserRowsTable = observer(function UserRowsTable({
  rowStore,
}: Props) {
  const isEditable =
    rowStore.isEditing && rowStore.type === TimeBillingTypeEnum.Manual;
  const userRows = rowStore.userRowsForDisplay;
  const timeMismatchMessage = t`Invoiced time and overservice must add up to tracked time`;
  const hasTimeMismatch = (row: UserRow) =>
    rowStore.userRowIdsWithTimeMismatch.has(row.id);

  return (
    <Box data-testid="user-rows-table" overflowX="auto">
      <Table size="sm" variant="simple">
        <Thead>
          <Tr>
            <Th>
              <Trans>User</Trans>
            </Th>
            <Th>
              <Trans>Position</Trans>
            </Th>
            <Th>
              <Trans>Team</Trans>
            </Th>
            <Th isNumeric>
              <Trans>Tracked Hours</Trans>
            </Th>
            <Th isNumeric>
              <Trans>Client Rate</Trans>
            </Th>
            <Th isNumeric>
              <HStack justify="flex-end" spacing="1">
                <Trans>Work Spent</Trans>
                <Tooltip
                  hasArrow
                  label={t`Value of tracked time calculated as tracked hours × client hourly rate`}
                  placement="top"
                  shouldWrapChildren
                >
                  <Icon name="info-circle" variant="line" boxSize="3" />
                </Tooltip>
              </HStack>
            </Th>
            <Th isNumeric>
              <HStack justify="flex-end" spacing="1">
                <Trans>Allocated Amount</Trans>
                <Tooltip
                  hasArrow
                  label={t`The portion of the total allocated value assigned to this team member based on their tracked hours`}
                  placement="top"
                  shouldWrapChildren
                >
                  <Icon name="info-circle" variant="line" boxSize="3" />
                </Tooltip>
              </HStack>
            </Th>
            <Th isNumeric>
              <Trans>Invoiced Time</Trans>
            </Th>
            <Th isNumeric>
              <Trans>Overservice</Trans>
            </Th>
            <Th isNumeric>
              <HStack justify="flex-end" spacing="1">
                <Trans>Eff. Rate (Invoiced)</Trans>
                <Tooltip
                  hasArrow
                  label={t`Calculated as Allocated amount divided by Invoiced time`}
                  placement="top"
                  shouldWrapChildren
                >
                  <Icon name="info-circle" variant="line" boxSize="3" />
                </Tooltip>
              </HStack>
            </Th>
            <Th isNumeric>
              <HStack justify="flex-end" spacing="1">
                <Trans>Eff. Rate (Total)</Trans>
                <Tooltip
                  hasArrow
                  label={t`Effective hourly rate calculated as Allocated Amount divided by total tracked hours`}
                  placement="top"
                  shouldWrapChildren
                >
                  <Icon name="info-circle" variant="line" boxSize="3" />
                </Tooltip>
              </HStack>
            </Th>
          </Tr>
        </Thead>

        <Tbody>
          {userRows.map((row, index) => (
            <Tr key={getRowKey(row)}>
              <Td>
                <HStack spacing="2">
                  <Avatar
                    name={row.user?.full_name}
                    size="xs"
                    src={getThumbnailUrl(row)}
                  />
                  <Text fontSize="sm">
                    {row.user?.full_name ?? t`Work Expense`}
                  </Text>
                </HStack>
              </Td>
              <Td>
                <Text
                  color={row.position ? undefined : "gray.400"}
                  fontSize="sm"
                >
                  {row.position?.title ?? "-"}
                </Text>
              </Td>
              <Td>
                <Text color={row.team ? undefined : "gray.400"} fontSize="sm">
                  {row.team?.name ?? "-"}
                </Text>
              </Td>
              <Td isNumeric>
                <Text fontSize="sm">{secondsToTime(row.tracked_time)}</Text>
              </Td>
              <Td isNumeric>
                <Text fontSize="sm">
                  {currency.formatByWorkspace(row.client_rate.denominatedValue)}
                </Text>
              </Td>
              <Td isNumeric>
                <Text fontSize="sm">
                  {currency.formatByWorkspace(row.spent.denominatedValue)}
                </Text>
              </Td>
              <Td isNumeric>
                {isEditable ? (
                  <Box minW="120px">
                    <NumberInput
                      value={row.amount.value.toString()}
                      currency
                      onChange={(val) =>
                        rowStore.updateUserRow(index, "amount", val)
                      }
                    />
                  </Box>
                ) : (
                  <Text fontSize="sm" fontWeight="medium">
                    {currency.formatByWorkspace(row.amount.denominatedValue)}
                  </Text>
                )}
              </Td>
              <Td isNumeric>
                <Tooltip
                  hasArrow
                  isDisabled={!hasTimeMismatch(row)}
                  label={timeMismatchMessage}
                  placement="top"
                  shouldWrapChildren
                >
                  {isEditable ? (
                    <Box minW="80px">
                      <TimeInput
                        data-testid="user-rows-table-invoiced-time-input"
                        isInvalid={hasTimeMismatch(row)}
                        onChange={(val) => {
                          if (val === undefined) return;
                          rowStore.updateUserRow(index, "invoiced_time", val);
                        }}
                        value={row.invoiced_time.value}
                      />
                    </Box>
                  ) : (
                    <Text
                      color={hasTimeMismatch(row) ? "red.500" : undefined}
                      fontSize="sm"
                    >
                      {row.invoiced_time.format()}
                    </Text>
                  )}
                </Tooltip>
              </Td>
              <Td isNumeric>
                <Tooltip
                  hasArrow
                  isDisabled={!hasTimeMismatch(row)}
                  label={timeMismatchMessage}
                  placement="top"
                  shouldWrapChildren
                >
                  {isEditable ? (
                    <Box minW="80px">
                      <TimeInput
                        data-testid="user-rows-table-overserviced-time-input"
                        isInvalid={hasTimeMismatch(row)}
                        onChange={(val) => {
                          if (val === undefined) return;
                          rowStore.updateUserRow(
                            index,
                            "overserviced_time",
                            val,
                          );
                        }}
                        value={row.overserviced_time.value}
                      />
                    </Box>
                  ) : (
                    <Text
                      color={
                        hasTimeMismatch(row) || row.overserviced_time.value > 0
                          ? "red.500"
                          : undefined
                      }
                      fontSize="sm"
                    >
                      {row.overserviced_time.format()}
                    </Text>
                  )}
                </Tooltip>
              </Td>
              <Td isNumeric>
                <Text fontSize="sm">
                  {currency.formatByWorkspace(
                    row.effective_hourly_rate_invoiced.denominatedValue,
                  )}
                </Text>
              </Td>
              <Td isNumeric>
                <Text fontSize="sm">
                  {currency.formatByWorkspace(
                    row.effective_hourly_rate.denominatedValue,
                  )}
                </Text>
              </Td>
            </Tr>
          ))}

          {/* Totals Row */}
          <Tr fontWeight="bold" bg="gray.50">
            <Td colSpan={3}>
              <Text fontSize="sm" fontWeight="bold">
                <Trans>Total</Trans>
              </Text>
            </Td>
            <Td isNumeric>
              <Button
                onClick={rowStore.openDistributionsModal}
                size="sm"
                variant="link"
              >
                <Text fontSize="sm">
                  {secondsToTime(rowStore.userRowsTotalTrackedTime)}
                </Text>
              </Button>
            </Td>
            <Td isNumeric>-</Td>
            <Td isNumeric>
              <Text fontSize="sm">
                {userRows.length > 0
                  ? currency.formatByWorkspace(
                      rowStore.userRowsTotalSpent.denominatedValue,
                    )
                  : "-"}
              </Text>
            </Td>
            <Td isNumeric>
              <HStack justify="flex-end" spacing="1">
                <Text
                  color={
                    rowStore.userRowsHaveTotalsMismatch ? "red.500" : undefined
                  }
                  fontSize="sm"
                >
                  {currency.formatByWorkspace(
                    rowStore.userRowsTotalAmount.denominatedValue,
                  )}
                </Text>

                {rowStore.userRowsHaveTotalsMismatch && (
                  <>
                    <Text color="red.500" fontSize="sm">
                      {rowStore.userRowsTotalDifference > 0 ? "+" : ""}
                      {currency.formatByWorkspace(
                        rowStore.userRowsTotalDifference,
                      )}
                    </Text>
                    <Tooltip
                      hasArrow
                      label={rowStore.userRowsTotalDifferenceMessage}
                      placement="top"
                      shouldWrapChildren
                    >
                      <Icon
                        name="info-circle"
                        variant="line"
                        color="red.500"
                        boxSize="4"
                      />
                    </Tooltip>
                  </>
                )}
              </HStack>
            </Td>
            <Td isNumeric>
              <Text fontSize="sm">
                {secondsToTime(rowStore.userRowsTotalInvoicedTime)}
              </Text>
            </Td>
            <Td isNumeric>
              <Text fontSize="sm">
                {secondsToTime(rowStore.userRowsTotalOverservicedTime)}
              </Text>
            </Td>
            <Td isNumeric>-</Td>
            <Td isNumeric>-</Td>
          </Tr>
        </Tbody>
      </Table>
    </Box>
  );
});
